/**
 * Fila de download INTEIRO de vídeos, para o Firefox.
 *
 * ## Por que existe
 *
 * O S3 só fala HTTP/1.1, e o navegador abre no máximo 6 conexões por host. No
 * Firefox, um `<video preload="metadata">` lê o começo do arquivo e SUSPENDE o
 * download sem fechar a conexão — e ela continua presa mesmo depois de o Vue
 * remover o elemento, até o coletor de lixo passar. Seis vídeos numa conversa
 * bastam: toda requisição seguinte ao S3 (imagens, fotos de perfil, a prévia)
 * fica pendente para sempre, sem erro nenhum. Reproduzido com 12 vídeos; o
 * Chrome não tem o problema.
 *
 * ## O que faz
 *
 * Em vez de deixar o `<video>` falar com o S3, baixa o arquivo INTEIRO por
 * `fetch` e entrega uma blob URL. Download que termina devolve a conexão; com
 * `concorrencia` abaixo de 6, sobra conexão para as imagens enquanto a fila
 * anda, e uma pilha de vídeos se resolve sozinha com o tempo.
 *
 * Reserva por contagem de referências: o mesmo vídeo em dois lugares (bolha e
 * miniatura da galeria) é um download só. Sem ninguém segurando, o que está na
 * fila sai dela, o que está baixando é abortado (libera a conexão) e o que já
 * terminou tem a blob URL revogada depois de `carenciaMs` — tempo para um
 * remount (troca de chave, rolagem virtual) reaproveitar sem baixar de novo.
 */

export class VideoGrandeDemaisError extends Error {}

export type EstadoDaReserva = 'fila' | 'baixando' | 'pronto' | 'falhou';

export interface Reserva {
    /** Blob URL pronta para o `src`. Rejeita em falha, vídeo grande demais ou desistência. */
    pronto: Promise<string>;
    estado: () => EstadoDaReserva;
    liberar: () => void;
}

export interface OpcoesDaFila {
    concorrencia: number;
    /** Acima disso não vale segurar em memória: o chamador cai para a URL direta. */
    limiteBytes: number;
    carenciaMs: number;
    fetch?: typeof fetch;
    criarUrl?: (blob: Blob) => string;
    revogarUrl?: (url: string) => void;
}

interface Entrada {
    url: string;
    refs: number;
    estado: EstadoDaReserva;
    pronto: Promise<string>;
    resolver: (blobUrl: string) => void;
    rejeitar: (erro: unknown) => void;
    blobUrl?: string;
    abortar?: AbortController;
    ouvintes: Set<(fracao: number) => void>;
    revogacao?: ReturnType<typeof setTimeout>;
}

const desistencia = () => new DOMException('Download de vídeo cancelado.', 'AbortError');

export function criarFilaDeVideos(opcoes: OpcoesDaFila) {
    const buscar = opcoes.fetch ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
    const criarUrl = opcoes.criarUrl ?? ((b: Blob) => URL.createObjectURL(b));
    const revogarUrl = opcoes.revogarUrl ?? ((u: string) => URL.revokeObjectURL(u));

    const entradas = new Map<string, Entrada>();
    const fila: Entrada[] = [];
    let ativos = 0;

    const avisar = (e: Entrada, fracao: number) => e.ouvintes.forEach((f) => f(fracao));

    async function baixar(e: Entrada) {
        e.estado = 'baixando';
        e.abortar = new AbortController();
        ativos++;
        try {
            // `no-store` pela mesma razão de `downloadAudioFile`: uma resposta opaca
            // de um <video> antigo no cache faria este fetch CORS falhar sem ir à rede.
            const resp = await buscar(e.url, { mode: 'cors', cache: 'no-store', signal: e.abortar.signal });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

            const total = Number(resp.headers.get('content-length')) || 0;
            if (total > opcoes.limiteBytes) {
                e.abortar.abort();
                throw new VideoGrandeDemaisError(`${total} bytes`);
            }

            const partes: Uint8Array[] = [];
            let recebidos = 0;
            const leitor = resp.body?.getReader();
            if (leitor) {
                for (;;) {
                    const { done, value } = await leitor.read();
                    if (done) break;
                    partes.push(value);
                    recebidos += value.byteLength;
                    if (recebidos > opcoes.limiteBytes) {
                        e.abortar.abort();
                        throw new VideoGrandeDemaisError(`> ${opcoes.limiteBytes} bytes`);
                    }
                    if (total) avisar(e, Math.min(1, recebidos / total));
                }
            } else {
                partes.push(new Uint8Array(await resp.arrayBuffer()));
            }

            const tipo = resp.headers.get('content-type') ?? '';
            e.blobUrl = criarUrl(new Blob(partes as BlobPart[], { type: tipo }));
            e.estado = 'pronto';
            avisar(e, 1);
            e.resolver(e.blobUrl);
        } catch (erro) {
            e.estado = 'falhou';
            if (entradas.get(e.url) === e) entradas.delete(e.url);
            e.rejeitar(erro);
        } finally {
            ativos--;
            bombear();
        }
    }

    function bombear() {
        while (ativos < opcoes.concorrencia && fila.length) void baixar(fila.shift()!);
    }

    function soltar(e: Entrada) {
        if (e.estado === 'fila') {
            fila.splice(fila.indexOf(e), 1);
            entradas.delete(e.url);
            e.estado = 'falhou';
            e.rejeitar(desistencia());
        } else if (e.estado === 'baixando') {
            entradas.delete(e.url);
            e.abortar?.abort(); // o catch de `baixar` rejeita e libera a vaga
        } else if (e.estado === 'pronto') {
            e.revogacao = setTimeout(() => {
                if (e.refs > 0) return;
                entradas.delete(e.url);
                if (e.blobUrl) revogarUrl(e.blobUrl);
            }, opcoes.carenciaMs);
        }
    }

    function reservar(url: string, onProgresso?: (fracao: number) => void): Reserva {
        let e = entradas.get(url);
        if (!e) {
            let resolver!: Entrada['resolver'];
            let rejeitar!: Entrada['rejeitar'];
            const pronto = new Promise<string>((res, rej) => { resolver = res; rejeitar = rej; });
            // Ninguém aguardando uma desistência não é erro não tratado.
            pronto.catch(() => undefined);
            e = { url, refs: 0, estado: 'fila', pronto, resolver, rejeitar, ouvintes: new Set() };
            entradas.set(url, e);
            fila.push(e);
            bombear();
        }

        const entrada = e;
        entrada.refs++;
        if (entrada.revogacao) { clearTimeout(entrada.revogacao); entrada.revogacao = undefined; }
        if (onProgresso) entrada.ouvintes.add(onProgresso);

        let liberada = false;
        return {
            pronto: entrada.pronto,
            estado: () => entrada.estado,
            liberar: () => {
                if (liberada) return;
                liberada = true;
                if (onProgresso) entrada.ouvintes.delete(onProgresso);
                if (--entrada.refs === 0) soltar(entrada);
            },
        };
    }

    return {
        reservar,
        /** Para teste/diagnóstico. */
        estatisticas: () => ({ ativos, naFila: fila.length, entradas: entradas.size }),
    };
}

/**
 * Fila do app. Concorrência 2: das 6 conexões por host, sobram 4 para imagens
 * e fotos de perfil enquanto os vídeos baixam. 64 MB cobre com folga o teto de
 * vídeo da Meta (16 MB) e a maior parte do que chega pela Evolution.
 */
export const filaDeVideos = criarFilaDeVideos({
    concorrencia: 2,
    limiteBytes: 64 * 1024 * 1024,
    carenciaMs: 30_000,
});
